import { useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Badge,
  Flex,
  Center,
  useColorMode,
} from "@chakra-ui/react";

import { Polygon } from "assets/svg/blockchains_dark";
import { Polygon_light } from "assets/svg/blockchains_light";

// Types
import { IIndexable } from "types/common";
import { NavItemProps } from "./Sidebar.types";

// Components
const Link = dynamic(() => import("components/Link"), {
  ssr: false,
});
const ACCORDIAN_ICON_MAPPING: IIndexable = {
  polygon: {
    light: Polygon_light,
    dark: Polygon,
  },
};

const NavItem = ({ menu, index, ...rest }: NavItemProps) => {
  const { key, label, icon_key, subMenus, disabled } = menu;
  const router = useRouter();
  const { colorMode } = useColorMode();

  const [, activeMenu, activeSubMenu] = useMemo(() => {
    return router.pathname.split("/");
  }, [router.pathname]);

  const isActive = useMemo(() => activeMenu === key, [key, activeMenu]);

  return (
    <AccordionItem
      isDisabled={disabled}
      borderTop="none"
      borderBottom="none"
      {...rest}
    >
      <AccordionButton
        px="0"
        fontFamily={isActive ? "medium" : "inherit"}
        color={isActive ? "brand.textColor" : "inherit"}
        border="none"
        _focus={{
          border: "none",
        }}
      >
        {icon_key ? (
          <Center
            bg="brand.primary"
            _dark={{
              bg: "#FFFFFF",
            }}
            borderRadius="50%"
            mr="1rem"
            width="20px"
            height="20px"
            border="1px solid"
            borderColor="brand.separator"
          >
            <Image
              src={ACCORDIAN_ICON_MAPPING[icon_key][colorMode]}
              alt=""
              height="14px"
              width="14px"
            />
          </Center>
        ) : null}

        <Box flex="1" textAlign="left">
          {label}
        </Box>
      </AccordionButton>

      <AccordionPanel pb={0} ml="1rem">
        {subMenus.map((subMenu: any) => {
          const isActiveSubMenu = activeSubMenu === subMenu.key;

          return (
            <Link link={subMenu.to} key={subMenu.key}>
              <Flex
                align="center"
                p="2"
                cursor={subMenu.disabled ? "not-allowed" : "pointer"}
                outline="none"
                fontFamily={isActiveSubMenu ? "medium" : "inherit"}
                color={isActiveSubMenu ? "brand.textColor" : "inherit"}
                pointerEvents={subMenu.disabled ? "none" : "auto"}
              >
                {subMenu.label}
                {subMenu.badge ? (
                  <Badge
                    ml="1rem"
                    bg="brand.primaryLight"
                    color="brand.primaryText"
                    borderWidth="1px"
                    borderColor="brand.primaryText"
                    fontSize="10px"
                    borderRadius="8px"
                  >
                    {subMenu.badge}
                  </Badge>
                ) : null}
              </Flex>
            </Link>
          );
        })}

        {index > 0 ? (
          <Box borderBottom="1px" mt="4" borderColor="brand.separator" />
        ) : null}
      </AccordionPanel>
    </AccordionItem>
  );
};

export default NavItem;
